import React, { useState, useEffect, useCallback, useRef } from 'react';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import { useCookies } from 'react-cookie';
import { Box, Typography, CircularProgress, Alert, Button, Pagination } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ClaimCard from './ClaimCard';
import { FILTER_TABS } from '../../claim-helpers-shared';

const PAGE_SIZE = 20;

const ClaimsOverview = ({ posConfig }) => {
    const [cookies] = useCookies(['secret', 'station', 'staff']);
    const [claims, setClaims] = useState([]);
    const [activeFilter, setActiveFilter] = useState('open');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const claimCardRefs = useRef({});
    const navigate = useNavigate();

    const socketUrl = posConfig?.socketUrl || process.env.REACT_APP_WEBSOCKET_URL;

    const { sendMessage, readyState } = useWebSocket(socketUrl, {
        queryParams: { station: cookies.station, secret: cookies.secret, staff: cookies.staff },
        onMessage: (message) => handleClaimWebsocketEvent(message),
        shouldReconnect: () => true,
        retryOnError: true,
    });

    const handleClaimWebsocketEvent = useCallback((message) => {
        try {
            const msg = JSON.parse(message.data);
            if (msg.task === 'respGetAllClaims') {
                if (msg.status === 'succeeded') { setClaims(msg.claims || []); setError(null); }
                else { setError(msg.message || 'Fehler.'); }
                setLoading(false);
                return;
            }
            const fwd = ['respNotFoundClaim', 'respRejectClaim', 'respSearchClaimShipments', 'respApproveClaim'];
            if (fwd.includes(msg.task)) {
                Object.values(claimCardRefs.current).forEach((h) => { if (typeof h === 'function') h(msg); });
            }
        } catch (e) { console.error('WS parse error:', e); }
    }, []);

    const loadClaims = useCallback((filter) => {
        setLoading(true);
        setPage(1);
        sendMessage(JSON.stringify({ task: 'getAllClaims', station: cookies.station, staff: cookies.staff, filter }));
    }, [sendMessage, cookies.station, cookies.staff]);

    useEffect(() => {
        if (readyState === ReadyState.OPEN) loadClaims(activeFilter);
    }, [readyState, activeFilter, loadClaims]);

    const tabIdx = FILTER_TABS.findIndex((t) => t.value === activeFilter);
    const totalPages = Math.ceil(claims.length / PAGE_SIZE);
    const paginated = claims.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    return (
        <Box sx={{ minHeight: '100vh' }}>
            <Box sx={{ maxWidth: 1200, mx: 'auto', px: 2.5, py: 2 }}>
                {/* Actions + filter tabs */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <Button size="small" variant="outlined" disableElevation
                        sx={{ borderRadius: 2, textTransform: 'none', fontSize: 12, fontWeight: 600, borderColor: '#ddd', color: '#1a1a1a', bgcolor: '#fff' }}
                        onClick={() => navigate('create')}>
                        + Neuer Suchauftrag
                    </Button>
                    <Box sx={{ flex: 1 }} />
                    {FILTER_TABS.map((tab, i) => (
                        <Button key={tab.value} size="small" disableElevation
                            variant={tabIdx === i ? 'contained' : 'text'}
                            sx={{
                                borderRadius: 2, textTransform: 'none', fontSize: 12, fontWeight: 600,
                                bgcolor: tabIdx === i ? '#1a1a1a' : 'transparent',
                                color: tabIdx === i ? '#fff' : '#777',
                                '&:hover': { bgcolor: tabIdx === i ? '#333' : '#e5e5e3' },
                                minWidth: 0, px: 2,
                            }}
                            onClick={() => setActiveFilter(tab.value)}>
                            {tab.label}
                        </Button>
                    ))}
                </Box>

                {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} /></Box>
                ) : claims.length === 0 ? (
                    <Typography sx={{ py: 6, textAlign: 'center', color: '#999', fontSize: 14 }}>Keine Suchaufträge gefunden.</Typography>
                ) : (
                    <>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {paginated.map((claim) => (
                                <ClaimCard
                                    key={claim.claimId}
                                    claim={claim}
                                    posConfig={posConfig}
                                    sendMessage={sendMessage}
                                    cookies={cookies}
                                    registerMessageHandler={(h) => { claimCardRefs.current[claim.claimId] = h; }}
                                    unregisterMessageHandler={() => { delete claimCardRefs.current[claim.claimId]; }}
                                />
                            ))}
                        </Box>
                        {totalPages > 1 && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                <Pagination count={totalPages} page={page} onChange={(_, v) => setPage(v)} size="small" />
                            </Box>
                        )}
                    </>
                )}
            </Box>
        </Box>
    );
};

export default ClaimsOverview;
